import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2, Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import SiteShell from "../site/SiteShell";
import PageHero from "../site/PageHero";
import { Reveal } from "../site/motion";
import { SITE, TRADES } from "../site/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INIT = { audience: "client", name: "", company: "", email: "", telephone: "", trade_required: "", project_location: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(INIT);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in Name, Email and Message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
      setForm(INIT);
      toast.success("Enquiry received — we'll be in touch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <SiteShell>
      <PageHero
        overline="Contact"
        title="Let's build better supply chains together."
        kicker="Request specialist contractors, discuss a procurement package, or explore partnership. We typically respond within one working day."
        image="https://images.unsplash.com/photo-1515263487990-61b07816b324?auto=format&fit=crop&w=1800&q=70"
      >
        <div className="flex flex-wrap gap-3">
          <a href="#form" className="vw-btn vw-btn--primary" data-testid="contact-form-cta">
            Send a Message <ArrowUpRight size={16}/>
          </a>
          <a href={`tel:${SITE.contact.phoneRaw}`} className="vw-btn vw-btn--ghost">
            Call {SITE.contact.phone}
          </a>
        </div>
      </PageHero>

      <section id="form" className="vw-section bg-[var(--vw-paper)]" data-testid="contact-section">
        <div className="vw-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 space-y-5">
            <Reveal>
              <div className="bg-[var(--vw-navy)] text-white p-8">
                <h3 className="font-display text-xl font-semibold mb-6">Get in touch</h3>
                <ul className="space-y-5 text-sm">
                  <li className="flex items-start gap-3" data-testid="contact-email-line">
                    <Mail size={18} className="text-[var(--vw-gold)] mt-0.5" />
                    <a href={`mailto:${SITE.contact.email}`} className="hover:text-[var(--vw-gold)]">{SITE.contact.email}</a>
                  </li>
                  <li className="flex items-start gap-3" data-testid="contact-phone-line">
                    <Phone size={18} className="text-[var(--vw-gold)] mt-0.5" />
                    <a href={`tel:${SITE.contact.phoneRaw}`} className="hover:text-[var(--vw-gold)]">{SITE.contact.phone}</a>
                  </li>
                  <li className="flex items-start gap-3" data-testid="contact-address-line">
                    <MapPin size={18} className="text-[var(--vw-gold)] mt-0.5" />
                    <span>{SITE.contact.addressLine1}<br/>{SITE.contact.addressLine2}</span>
                  </li>
                </ul>
                <div className="h-px bg-white/10 my-7" />
                <div className="font-mono text-[11px] tracking-[0.22em] text-white/60 uppercase">Founded {SITE.contact.founded}</div>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">
                  Verified Workforce — a UK construction supply-chain and procurement business.
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.08}>
              <div className="border border-[var(--vw-line)] overflow-hidden">
                <iframe
                  title="Verified Workforce — Bromley, UK"
                  src="https://www.google.com/maps?q=Regus+Bromley+UK&output=embed"
                  width="100%"
                  height="280"
                  style={{ border: 0, display: "block" }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="contact-map"
                />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <div className="bg-white p-8 lg:p-10 border border-[var(--vw-line)]">
                {done ? (
                  <div className="text-center py-14" data-testid="contact-success">
                    <div className="w-16 h-16 mx-auto flex items-center justify-center bg-[var(--vw-blue)]/10 text-[var(--vw-blue)] mb-6">
                      <CheckCircle2 size={30}/>
                    </div>
                    <h3 className="font-display text-2xl font-semibold text-[var(--vw-navy)]">Enquiry received</h3>
                    <p className="mt-3 text-[var(--vw-muted)]">A member of the team will respond within one working day.</p>
                    <Link to="/" className="vw-btn vw-btn--outline-dark mt-8">Back to Home</Link>
                  </div>
                ) : (
                  <form onSubmit={submit} data-testid="contact-form">
                    <div className="mb-6">
                      <label className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--vw-navy)] mb-3 block">I am a</label>
                      <div className="grid grid-cols-2 gap-2">
                        {[["client","Client / Main Contractor"],["partner","Specialist Contractor"]].map(([v,l]) => (
                          <button
                            type="button"
                            key={v}
                            onClick={() => setForm((f)=>({...f, audience:v}))}
                            className={`px-4 py-3 text-sm border transition-colors text-left ${form.audience === v ? "border-[var(--vw-blue)] bg-[var(--vw-blue)]/5 text-[var(--vw-navy)]" : "border-[var(--vw-line)] bg-white text-[var(--vw-charcoal)] hover:border-[var(--vw-blue)]"}`}
                            data-testid={`contact-audience-${v}`}
                          >
                            {l}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                      <F label="Name *" name="name" value={form.name} onChange={onChange} required testid="contact-name"/>
                      <F label="Company" name="company" value={form.company} onChange={onChange} testid="contact-company"/>
                      <F label="Email *" type="email" name="email" value={form.email} onChange={onChange} required testid="contact-email"/>
                      <F label="Telephone" name="telephone" value={form.telephone} onChange={onChange} testid="contact-telephone"/>
                      <div className="vw-field">
                        <label htmlFor="contact-trade">Trade Required / Specialism</label>
                        <select id="contact-trade" name="trade_required" value={form.trade_required} onChange={onChange} data-testid="contact-trade-required">
                          <option value="">Select…</option>
                          {TRADES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <F label="Project Location" name="project_location" value={form.project_location} onChange={onChange} placeholder="e.g. Croydon, SE London" testid="contact-project-location"/>
                      <div className="vw-field sm:col-span-2">
                        <label htmlFor="contact-message">Message *</label>
                        <textarea id="contact-message" name="message" rows={5} value={form.message} onChange={onChange} required placeholder="Tell us about your project or your business…" data-testid="contact-message"/>
                      </div>
                    </div>
                    <button type="submit" disabled={loading} className="vw-btn vw-btn--primary mt-6" data-testid="contact-submit-btn">
                      {loading ? <><Loader2 size={16} className="animate-spin"/> Sending…</> : "Send Enquiry"}
                    </button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}

function F({ label, name, value, onChange, type = "text", required, placeholder, testid }) {
  const id = `field-${testid || name}`;
  return (
    <div className="vw-field">
      <label htmlFor={id}>{label}</label>
      <input id={id} name={name} type={type} value={value} onChange={onChange} required={required} placeholder={placeholder} data-testid={testid}/>
    </div>
  );
}
